import React, { useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Download, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/** Diálogo para exportar cirugías a Excel filtrando por mes+año o rango completo. */
export function ExportExcelDialog({ open, onClose }) {
  const now = moment();
  const [year, setYear] = useState(String(now.year()));
  const [month, setMonth] = useState(String(now.month() + 1)); // 1..12
  const [scope, setScope] = useState('month'); // 'month' | 'all'
  const [downloading, setDownloading] = useState(false);

  const years = [];
  for (let y = now.year(); y >= now.year() - 5; y--) years.push(y);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (scope === 'month') {
        const m = String(month).padStart(2, '0');
        const first = `${year}-${m}-01`;
        const last = moment(`${year}-${m}-01`).endOf('month').format('YYYY-MM-DD');
        params.start_date = first;
        params.end_date = last;
      }
      const response = await axios.get(`${API_URL}/api/export/surgeries.xlsx`, {
        withCredentials: true,
        responseType: 'blob',
        params,
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix = scope === 'month' ? `${year}-${String(month).padStart(2, '0')}` : 'todas';
      link.setAttribute('download', `cirugias_${suffix}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al exportar');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-sm" data-testid="export-excel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A1C18]">
            <Download className="w-5 h-5 text-[#2B4C3B]" />
            Exportar a Excel
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Rango a exportar</Label>
            <div className="flex gap-2 mt-1.5">
              <button
                type="button"
                onClick={() => setScope('month')}
                data-testid="export-scope-month"
                className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  scope === 'month'
                    ? 'border-[#2B4C3B] bg-[#EDF5F0] text-[#2B4C3B] font-medium'
                    : 'border-[#E8E5DD] text-[#6A736B] hover:bg-[#FDFBF7]'
                }`}
              >
                <Calendar className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                Mes específico
              </button>
              <button
                type="button"
                onClick={() => setScope('all')}
                data-testid="export-scope-all"
                className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  scope === 'all'
                    ? 'border-[#2B4C3B] bg-[#EDF5F0] text-[#2B4C3B] font-medium'
                    : 'border-[#E8E5DD] text-[#6A736B] hover:bg-[#FDFBF7]'
                }`}
              >
                Todas
              </button>
            </div>
          </div>

          {scope === 'month' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="export-month">Mes</Label>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger id="export-month" data-testid="export-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="export-year">Año</Label>
                <Select value={year} onValueChange={setYear}>
                  <SelectTrigger id="export-year" data-testid="export-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="export-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white"
            data-testid="export-confirm-btn"
          >
            {downloading ? (
              <><Loader2 size={14} className="animate-spin mr-1.5" />Generando...</>
            ) : (
              <><Download size={14} className="mr-1.5" />Descargar</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
